/**
 * ButtonWidget Component
 *
 * Renders a configurable button widget for custom actions.
 * Preview mode only - button is disabled in the editor.
 */

import React from 'react';
import { ButtonWidgetProps } from './types';
import { BUTTON_DEFAULT_CONFIG, ButtonStyle, ButtonSize } from '../../config/widgetDefaults';

// Style mappings for button variants
const styleClasses: Record<ButtonStyle, string> = {
  primary: 'tw-bg-accent-bold tw-text-ink-inverse tw-border-accent-bold hover:tw-bg-accent-bold-hover',
  secondary: 'tw-bg-surface tw-text-accent-bold tw-border-accent-bold hover:tw-bg-accent-tint',
  tertiary: 'tw-bg-transparent tw-text-accent-bold tw-border-transparent hover:tw-bg-accent-tint',
  outline: 'tw-bg-surface tw-text-ink tw-border-edge-strong hover:tw-bg-surface-hover',
};

// Size mappings
const sizeClasses: Record<ButtonSize, string> = {
  small: 'tw-px-2 tw-py-1 tw-text-xs',
  medium: 'tw-px-4 tw-py-2 tw-text-sm',
  large: 'tw-px-6 tw-py-3 tw-text-base',
};

export const ButtonWidget = ({
  config,
  isSelected,
  onSelect,
}: ButtonWidgetProps) => {
  // Merge provided config with defaults
  const mergedConfig = {
    ...BUTTON_DEFAULT_CONFIG,
    ...config,
  };

  const {
    buttonLabel,
    displayName,
    className,
    style,
    size,
    showIcon,
    iconPosition,
    iconUrl,
    fullWidth,
  } = mergedConfig;

  // Use buttonLabel if available, fall back to displayName
  const label = buttonLabel || displayName || 'Button';

  // Build button classes
  const buttonClasses = [
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-2',
    'tw-border tw-rounded tw-font-semibold',
    'tw-transition-colors tw-duration-200',
    'tw-cursor-not-allowed tw-opacity-80', // Disabled state in editor
    styleClasses[style] || styleClasses.primary,
    sizeClasses[size] || sizeClasses.medium,
    fullWidth ? 'tw-w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Render icon if configured
  const renderIcon = () => {
    if (!showIcon || !iconUrl) return null;
    return (
      <img
        src={iconUrl}
        alt=""
        className="tw-w-4 tw-h-4 tw-object-contain"
      />
    );
  };

  return (
    <div role="button" data-testid="widgets-buttonwidget-div-1"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`tw-py-2 tw-cursor-pointer tw-transition-all ${
        isSelected ? 'tw-ring-2 tw-ring-accent tw-ring-offset-2 tw-rounded' : ''
      }`}
    >
      <button data-testid="widgets-buttonwidget-button-1"
        type="button"
        disabled
        className={buttonClasses}
        aria-label={label}
      >
        {showIcon && iconPosition === 'left' && renderIcon()}
        <span>{label}</span>
        {showIcon && iconPosition === 'right' && renderIcon()}
      </button>
    </div>
  );
};

export default ButtonWidget;
